// src/components/ProcessDialog/DesktopResults.jsx
import React from 'react';
import {
    Grid,
    Stack,
    Typography,
    TableContainer,
    Paper,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Card,
    Divider,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ShieldCheck } from 'lucide-react';
import { PiTestTubeDuotone } from 'react-icons/pi'; 
import {
    formatValue,
    fmtAbsWithLog,
    getDeltaChip,
} from '../../../utils/mathFormatters';

/**
 * `secoes` vem de utils/predicao secoesDoResultado: uma por bacteria/alvo, com a
 * contagem do modelo (`predLog`) e a do laboratorio (`labLog`) na mesma `unidade`.
 * `agua` = alvo regido pela Portaria 888 (so ele tem "em 100 mL" / conforme).
 */
const DesktopResults = ({ isPresenceMode, isSubmitting, secoes = [] }) => {
    const theme = useTheme();

    const renderPresenceCard = (key, title, presence, agua) => {
        const isPending = presence === undefined || isSubmitting;
        const isPresenca = presence === true;

        let iconPanelBg = alpha(theme.palette.text.primary, 0.04);
        let iconColor = alpha(theme.palette.text.primary, 0.3);
        let cardBg = 'background.paper';

        if (!isPending) {
            if (isPresenca) {
                iconPanelBg = alpha(theme.palette.error.main, 0.06);
                iconColor = theme.palette.error.main;
                cardBg = alpha(theme.palette.error.main, 0.06);
            } else {
                iconPanelBg = alpha(theme.palette.success.main, 0.06);
                iconColor = theme.palette.success.main;
                cardBg = alpha(theme.palette.success.main, 0.06);
            }
        }

        return (
            <Grid item xs={12} md={6} key={key}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: isPending
                            ? 'divider'
                            : isPresenca
                              ? alpha(theme.palette.error.main, 0.3)
                              : alpha(theme.palette.success.main, 0.3),
                        bgcolor: cardBg,
                        overflow: 'hidden',
                    }}
                >
                    <Stack direction="row" sx={{ height: '100%' }}>
                        <Box
                            sx={{
                                width: 70,
                                bgcolor: iconPanelBg,
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {/* ÍCONE DUOTONE - Aumentei para size 32 para ter mais destaque */}
                            <PiTestTubeDuotone size={32} color={iconColor} />
                        </Box>
                        <Box sx={{ flexGrow: 1, p: 2 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{ textTransform: 'uppercase' }}
                            >
                                {title}
                            </Typography>
                            <Typography
                                variant="h5"
                                fontWeight={800}
                                color={
                                    isPending
                                        ? 'text.disabled'
                                        : isPresenca
                                          ? 'error.main'
                                          : 'success.main'
                                }
                            >
                                {isPending
                                    ? 'PROCESSANDO...'
                                    : isPresenca
                                      ? 'PRESENÇA'
                                      : 'AUSÊNCIA'}
                                {!isPending && agua && (
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        fontWeight={700}
                                        sx={{ ml: 1, opacity: 0.8 }}
                                    >
                                        em 100 mL
                                    </Typography>
                                )}
                            </Typography>

                            {!isPending && agua && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Divider sx={{ mb: 1, opacity: 0.6 }} />
                                    <Stack spacing={0.5}>
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <ShieldCheck
                                                size={14}
                                                color={
                                                    theme.palette.text.secondary
                                                }
                                            />
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight={500}
                                            >
                                                Padrão Portaria 888:{' '}
                                                {isPresenca
                                                    ? 'Presença em 100 mL'
                                                    : 'Ausência em 100 mL'}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            variant="caption"
                                            fontWeight={800}
                                            color={
                                                isPresenca
                                                    ? 'error.main'
                                                    : 'success.main'
                                            }
                                        >
                                            STATUS:{' '}
                                            {isPresenca
                                                ? 'NÃO CONFORME'
                                                : 'CONFORME'}
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </Card>
            </Grid>
        );
    };

    if (isPresenceMode) {
        return (
            <Grid container spacing={2}>
                {secoes.map((s) => renderPresenceCard(s.chave, s.titulo, s.presenca, s.agua))}
            </Grid>
        );
    }

    return (
        <Stack spacing={3}>
            {secoes.map((s) => ({
                key: s.chave,
                title: s.titulo,
                nota:
                    s.labValor != null && !s.labComparavel
                        ? `Laboratório em ${s.labUnidade || 'outra unidade'} — não comparável com a predição em ${s.unidade}.`
                        : null,
                rows: [
                    {
                        label: `Log ${s.unidade || ''}`.trim(),
                        labNmp: s.labLog,
                        predNmp: s.predLog,
                        type: 'NMP',
                    },
                ],
            })).map((section, idx) => (
                <TableContainer
                    key={section.key || idx}
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 3 }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'rgba(0,0,0,0.02)',
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700}>
                            {section.title}
                        </Typography>
                        {section.nota && (
                            <Typography variant="caption" color="text.secondary">
                                {section.nota}
                            </Typography>
                        )}
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.01)' }}>
                                <TableCell sx={{ py: 0.5 }}>
                                    <strong>Métrica</strong>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <strong>Lab</strong>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <strong>IA (PRED)</strong>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <strong>Status</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {section.rows.map((row, rIdx) => (
                                <TableRow key={rIdx}>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={600}
                                        >
                                            {row.label}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Typography variant="caption">
                                            {formatValue(
                                                row.type === 'NMP'
                                                    ? row.labNmp
                                                    : row.labUfc,
                                                fmtAbsWithLog,
                                                false
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            color="secondary.main"
                                        >
                                            {formatValue(
                                                row.type === 'NMP'
                                                    ? row.predNmp
                                                    : row.predUfc,
                                                fmtAbsWithLog,
                                                isSubmitting
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                        {getDeltaChip(
                                            row.type === 'NMP'
                                                ? row.predNmp
                                                : row.predUfc,
                                            row.type === 'NMP'
                                                ? row.labNmp
                                                : row.labUfc,
                                            isSubmitting
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ))}
        </Stack>
    );
};

export default DesktopResults;
